import React, { useEffect, useMemo, useState } from 'react';
import { CircleDot, Gamepad2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';

type RetroPlayGamepadDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preferredGamepadId?: string | null;
};

type GamepadSnapshot = {
  id: string;
  index: number;
  mapping: string;
  connected: boolean;
  buttons: Array<{ index: number; pressed: boolean; value: number }>;
  axes: Array<{ index: number; value: number }>;
};

const DEADZONE = 0.08;

const BUTTON_LABELS: Array<{ label: string; type: 'button' | 'axis'; index: number }> = [
  { label: 'B1', type: 'button', index: 0 },
  { label: 'B2', type: 'button', index: 1 },
  { label: 'B3', type: 'button', index: 2 },
  { label: 'B4', type: 'button', index: 3 },
  { label: 'L1', type: 'button', index: 4 },
  { label: 'R1', type: 'button', index: 5 },
  { label: 'L2', type: 'button', index: 6 },
  { label: 'R2', type: 'button', index: 7 },
  { label: 'S1', type: 'button', index: 8 },
  { label: 'S2', type: 'button', index: 9 },
  { label: 'L3', type: 'button', index: 10 },
  { label: 'R3', type: 'button', index: 11 },
  { label: 'DU', type: 'button', index: 12 },
  { label: 'DD', type: 'button', index: 13 },
  { label: 'DL', type: 'button', index: 14 },
  { label: 'DR', type: 'button', index: 15 },
  { label: 'LX', type: 'axis', index: 0 },
  { label: 'LY', type: 'axis', index: 1 },
  { label: 'RX', type: 'axis', index: 2 },
  { label: 'RY', type: 'axis', index: 3 },
];

const formatAxisValue = (value: number) => {
  const normalized = Math.abs(value) < DEADZONE ? 0 : value;
  return normalized.toFixed(2);
};

const formatButtonValue = (value: number) => value.toFixed(2);

const parseVendorProduct = (id: string) => {
  const match = id.match(/Vendor:\s*([0-9a-fA-F]{4}).*Product:\s*([0-9a-fA-F]{4})/i);
  if (!match) {
    return null;
  }

  return {
    vendorId: `0x${match[1].toUpperCase()}`,
    productId: `0x${match[2].toUpperCase()}`,
  };
};

const stripVendorProduct = (id: string) => id.replace(/\s*\(Vendor:.*Product:.*\)\s*/i, '').trim();

const getGamepadSnapshots = (): GamepadSnapshot[] => {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') {
    return [];
  }

  return Array.from(navigator.getGamepads())
    .filter((gamepad): gamepad is Gamepad => Boolean(gamepad))
    .map((gamepad) => ({
      id: gamepad.id,
      index: gamepad.index,
      mapping: gamepad.mapping || 'n/a',
      connected: gamepad.connected,
      buttons: gamepad.buttons.map((button, index) => ({
        index,
        pressed: button.pressed,
        value: button.value,
      })),
      axes: gamepad.axes.map((value, index) => ({
        index,
        value: Math.abs(value) < DEADZONE ? 0 : value,
      })),
    }));
};

const buttonValue = (gamepad: GamepadSnapshot | null, index: number) => {
  const value = gamepad?.buttons[index]?.value ?? 0;
  return formatButtonValue(value);
};

const buttonPressed = (gamepad: GamepadSnapshot | null, index: number) => Boolean(gamepad?.buttons[index]?.pressed);

const axisValue = (gamepad: GamepadSnapshot | null, index: number) => gamepad?.axes[index]?.value ?? 0;

const CompactTelemetryCell = ({
  label,
  value,
  active,
}: {
  label: string;
  value: string;
  active: boolean;
}) => (
  <div
    className={cn(
      'rounded-md border px-2 py-1.5 text-center transition-colors',
      active ? 'border-primary/60 bg-primary/10 text-primary' : 'border-border/60 bg-muted/20 text-muted-foreground'
    )}
  >
    <div className="text-[10px] font-semibold uppercase tracking-wide">{label}</div>
    <div className="mt-0.5 text-xs font-mono">{value}</div>
  </div>
);

const StickVisualizer = ({ title, x, y }: { title: string; x: number; y: number }) => {
  const clampedX = Math.max(-1, Math.min(1, Math.abs(x) < DEADZONE ? 0 : x));
  const clampedY = Math.max(-1, Math.min(1, Math.abs(y) < DEADZONE ? 0 : y));
  const left = `${50 + clampedX * 38}%`;
  const top = `${50 + clampedY * 38}%`;

  return (
    <div className="space-y-2">
      <div className="text-center text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="relative mx-auto h-28 w-28 rounded-full border border-border/70 bg-muted/10">
        <div className="absolute left-1/2 top-1 h-[calc(100%-0.5rem)] w-px -translate-x-1/2 bg-border/60" />
        <div className="absolute left-1 top-1/2 h-px w-[calc(100%-0.5rem)] -translate-y-1/2 bg-border/60" />
        <div
          className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-rose-500 shadow-[0_0_12px_rgba(244,63,94,0.55)] transition-all"
          style={{ left, top }}
        />
      </div>
      <div className="text-center font-mono text-xs text-muted-foreground">
        X {formatAxisValue(clampedX)} · Y {formatAxisValue(clampedY)}
      </div>
    </div>
  );
};

const CompactControllerFigure = ({ gamepad }: { gamepad: GamepadSnapshot | null }) => {
  const leftStickPressed = buttonPressed(gamepad, 10);
  const rightStickPressed = buttonPressed(gamepad, 11);
  const dpadUp = buttonPressed(gamepad, 12);
  const dpadDown = buttonPressed(gamepad, 13);
  const dpadLeft = buttonPressed(gamepad, 14);
  const dpadRight = buttonPressed(gamepad, 15);

  const inputClass = (active: boolean) =>
    cn(
      'absolute flex items-center justify-center rounded-full border text-[10px] font-bold transition-colors',
      active ? 'border-emerald-400/80 bg-emerald-500/20 text-emerald-200' : 'border-zinc-500 bg-zinc-100 text-zinc-800'
    );

  const centerButtonClass = (active: boolean) =>
    cn(
      'absolute rounded-full border transition-colors',
      active ? 'border-emerald-400/80 bg-emerald-500/20' : 'border-zinc-500 bg-zinc-100'
    );

  return (
    <div className="space-y-3">
      <div className="mx-auto flex w-fit items-center justify-center rounded-full bg-zinc-200 px-3 py-1 text-xs font-bold text-zinc-900">
        P1
      </div>
      <div className="relative mx-auto aspect-[1.3/1] w-full max-w-[360px]">
        <svg viewBox="0 0 360 276" className="absolute inset-0 h-full w-full">
          <path
            d="M76 64C98 45 132 42 158 54L180 66L202 54C228 42 262 45 284 64L307 84C326 100 337 126 337 154V214C337 240 316 261 290 261C278 261 266 256 257 247L215 205C206 196 193 191 180 191C167 191 154 196 145 205L103 247C94 256 82 261 70 261C44 261 23 240 23 214V154C23 126 34 100 53 84L76 64Z"
            fill="#ECECEC"
            stroke="#A1A1AA"
            strokeWidth="2"
          />
          <path d="M86 57C96 43 118 39 139 45" fill="none" stroke="#D4D4D8" strokeWidth="10" strokeLinecap="round" />
          <path d="M274 57C264 43 242 39 221 45" fill="none" stroke="#D4D4D8" strokeWidth="10" strokeLinecap="round" />
        </svg>

        <div className={cn(inputClass(buttonPressed(gamepad, 4)), 'left-[20%] top-[10%] h-5 w-12 rounded-full')}>L1</div>
        <div className={cn(inputClass(buttonPressed(gamepad, 5)), 'right-[20%] top-[10%] h-5 w-12 rounded-full')}>R1</div>
        <div className={cn(inputClass(buttonPressed(gamepad, 6)), 'left-[16%] top-[4%] h-5 w-12 rounded-full')}>L2</div>
        <div className={cn(inputClass(buttonPressed(gamepad, 7)), 'right-[16%] top-[4%] h-5 w-12 rounded-full')}>R2</div>

        <div className={cn(inputClass(buttonPressed(gamepad, 2)), 'left-[57%] top-[24%] h-8 w-8')}>X</div>
        <div className={cn(inputClass(buttonPressed(gamepad, 0)), 'left-[66%] top-[32%] h-8 w-8')}>A</div>
        <div className={cn(inputClass(buttonPressed(gamepad, 1)), 'left-[75%] top-[24%] h-8 w-8')}>B</div>
        <div className={cn(inputClass(buttonPressed(gamepad, 3)), 'left-[66%] top-[16%] h-8 w-8')}>Y</div>

        <div className={cn(centerButtonClass(buttonPressed(gamepad, 8)), 'left-[43%] top-[28%] h-4 w-4')} />
        <div className={cn(centerButtonClass(buttonPressed(gamepad, 9)), 'left-[53%] top-[28%] h-4 w-4')} />
        <div className={cn(centerButtonClass(buttonPressed(gamepad, 16)), 'left-[48%] top-[35%] h-5 w-5')} />

        <div className="absolute left-[18%] top-[23%] h-12 w-12 rounded-full border border-zinc-500 bg-zinc-100" />
        <div className="absolute left-[57%] top-[48%] h-14 w-14 rounded-full border border-zinc-500 bg-zinc-100" />
        <div className={cn(centerButtonClass(leftStickPressed), 'left-[20.5%] top-[25.5%] h-7 w-7')} />
        <div className={cn(centerButtonClass(rightStickPressed), 'left-[61%] top-[52%] h-7 w-7')} />

        <div className="absolute left-[27%] top-[44%] h-14 w-14 rounded-full border border-zinc-500 bg-zinc-100">
          <div className={cn('absolute left-1/2 top-1 h-[calc(100%-0.5rem)] w-3 -translate-x-1/2 rounded-sm', dpadUp || dpadDown ? 'bg-emerald-500/30' : 'bg-zinc-200')} />
          <div className={cn('absolute left-1 top-1/2 h-3 w-[calc(100%-0.5rem)] -translate-y-1/2 rounded-sm', dpadLeft || dpadRight ? 'bg-emerald-500/30' : 'bg-zinc-200')} />
          <div className={cn('absolute left-1/2 top-1 h-5 w-3 -translate-x-1/2 rounded-t-sm', dpadUp ? 'bg-emerald-500' : 'bg-zinc-300')} />
          <div className={cn('absolute bottom-1 left-1/2 h-5 w-3 -translate-x-1/2 rounded-b-sm', dpadDown ? 'bg-emerald-500' : 'bg-zinc-300')} />
          <div className={cn('absolute left-1 top-1/2 h-3 w-5 -translate-y-1/2 rounded-l-sm', dpadLeft ? 'bg-emerald-500' : 'bg-zinc-300')} />
          <div className={cn('absolute right-1 top-1/2 h-3 w-5 -translate-y-1/2 rounded-r-sm', dpadRight ? 'bg-emerald-500' : 'bg-zinc-300')} />
        </div>
      </div>
    </div>
  );
};

export const RetroPlayGamepadDialog: React.FC<RetroPlayGamepadDialogProps> = ({
  open,
  onOpenChange,
  preferredGamepadId,
}) => {
  const [gamepads, setGamepads] = useState<GamepadSnapshot[]>([]);
  const [selectedGamepadIndex, setSelectedGamepadIndex] = useState<string>('');

  useEffect(() => {
    if (!open) {
      return;
    }

    const syncGamepads = () => {
      const nextGamepads = getGamepadSnapshots();
      setGamepads(nextGamepads);
      setSelectedGamepadIndex((current) => {
        if (current && nextGamepads.some((gamepad) => String(gamepad.index) === current)) {
          return current;
        }

        const preferredGamepad = preferredGamepadId
          ? nextGamepads.find((gamepad) => gamepad.id === preferredGamepadId)
          : null;

        return preferredGamepad ? String(preferredGamepad.index) : String(nextGamepads[0]?.index ?? '');
      });
    };

    syncGamepads();
    const intervalId = window.setInterval(syncGamepads, 100);
    window.addEventListener('gamepadconnected', syncGamepads);
    window.addEventListener('gamepaddisconnected', syncGamepads);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('gamepadconnected', syncGamepads);
      window.removeEventListener('gamepaddisconnected', syncGamepads);
    };
  }, [open, preferredGamepadId]);

  const selectedGamepad = useMemo(
    () => gamepads.find((gamepad) => String(gamepad.index) === selectedGamepadIndex) || gamepads[0] || null,
    [gamepads, selectedGamepadIndex]
  );

  const gamepadName = selectedGamepad ? stripVendorProduct(selectedGamepad.id) || `Controller ${selectedGamepad.index + 1}` : '';
  const ids = selectedGamepad ? parseVendorProduct(selectedGamepad.id) : null;
  const supportsGamepads = typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[min(94vw,580px)] max-w-[580px] overflow-hidden border-border/70 bg-slate-950 p-0 text-slate-100">
        <DialogTitle className="sr-only">Retro Play gamepad tester</DialogTitle>
        <DialogDescription className="sr-only">
          A compact controller-centered tester showing live gamepad detection, buttons, and stick movement.
        </DialogDescription>

        <div className="flex max-h-[92vh] flex-col">
          <div className="border-b border-slate-800 px-4 py-3 pr-14">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Gamepad2 className="h-4 w-4 text-sky-400" />
                <div>
                  <div className="text-sm font-semibold">Gamepad Tester</div>
                  <div className="text-xs text-slate-400">Compact live input view for Retro Play</div>
                </div>
              </div>
              <Badge className={cn('mr-1 border-0', selectedGamepad ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-100')}>
                {selectedGamepad ? 'Connected' : 'Waiting'}
              </Badge>
            </div>
          </div>

          <div className="space-y-3 overflow-y-auto px-4 py-3">
            {!supportsGamepads ? (
              <div className="rounded-xl border border-slate-800 bg-slate-900/80 px-4 py-6 text-sm text-slate-300">
                This environment does not expose the browser Gamepad API.
              </div>
            ) : null}

            {supportsGamepads && gamepads.length === 0 ? (
              <div className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/80 px-4 py-6 text-sm text-slate-300">
                <div className="font-medium text-slate-100">No controller detected</div>
                <ul className="list-disc space-y-1 pl-5 text-slate-400">
                  <li>Reconnect the controller and reopen the tester.</li>
                  <li>Click inside Retro Play once so Chromium grants focus.</li>
                  <li>For 8BitDo on Windows, XInput mode is usually the most reliable.</li>
                </ul>
              </div>
            ) : null}

            {supportsGamepads && gamepads.length > 0 ? (
              <>
                <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                  <Select value={selectedGamepad ? String(selectedGamepad.index) : ''} onValueChange={setSelectedGamepadIndex}>
                    <SelectTrigger className="h-9 border-slate-800 bg-slate-900 text-slate-100">
                      <SelectValue placeholder="Select a connected controller" />
                    </SelectTrigger>
                    <SelectContent>
                      {gamepads.map((gamepad) => (
                        <SelectItem key={gamepad.index} value={String(gamepad.index)}>
                          Controller {gamepad.index + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <CircleDot className="h-3.5 w-3.5 text-emerald-400" />
                    {gamepads.length} detected
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-800 bg-[#0b1018] px-3 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
                  <CompactControllerFigure gamepad={selectedGamepad} />

                  {selectedGamepad ? (
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-center">
                      <Badge className="border-0 bg-green-950 text-green-300">{gamepadName}</Badge>
                      {ids ? <Badge className="border-0 bg-amber-950 text-amber-300">VID: {ids.vendorId}</Badge> : null}
                      {ids ? <Badge className="border-0 bg-amber-950 text-amber-300">PID: {ids.productId}</Badge> : null}
                    </div>
                  ) : null}

                  {selectedGamepad ? (
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-[11px]">
                      <Badge variant="secondary" className="bg-slate-800 text-slate-200">{selectedGamepad.mapping === 'standard' ? 'Standard Mapping' : 'Direct Mapping'}</Badge>
                      <Badge variant="secondary" className="bg-slate-800 text-slate-200">Index {selectedGamepad.index}</Badge>
                    </div>
                  ) : null}
                </div>

                {selectedGamepad ? (
                  <div className="grid grid-cols-4 gap-1.5 rounded-2xl border border-slate-800 bg-slate-900/80 p-2 sm:grid-cols-5">
                    {BUTTON_LABELS.map((item) => {
                      const isButton = item.type === 'button';
                      const active = isButton
                        ? buttonPressed(selectedGamepad, item.index)
                        : Math.abs(axisValue(selectedGamepad, item.index)) >= DEADZONE;
                      const value = isButton
                        ? buttonValue(selectedGamepad, item.index)
                        : formatAxisValue(axisValue(selectedGamepad, item.index));

                      return <CompactTelemetryCell key={`${item.type}-${item.index}`} label={item.label} value={value} active={active} />;
                    })}
                  </div>
                ) : null}

                {selectedGamepad ? (
                  <div className="grid grid-cols-2 gap-3 rounded-2xl border border-slate-800 bg-slate-900/80 px-3 py-4">
                    <StickVisualizer title="Left Stick" x={axisValue(selectedGamepad, 0)} y={axisValue(selectedGamepad, 1)} />
                    <StickVisualizer title="Right Stick" x={axisValue(selectedGamepad, 2)} y={axisValue(selectedGamepad, 3)} />
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
